import { useCallback, useState } from "react";
import { Button, Typography, message } from "antd";
import PageTitle from "../../components/title/pageTitle";
import Navigation from "../../components/navigation/navigation";
import Footer from "../../components/footer/footer";
import BackHomeIcon, { PANEL_PATH } from "../../components/icons/BackHome/backhome";
import VideoReviewFormFields from "../../components/videoReview/VideoReviewFormFields";
import VideoReviewSharePanel from "../../components/videoReview/VideoReviewSharePanel";
import copyToClipboard from "../../utils/copy";
import { buildVideoReviewUrl } from "../../utils/videoReviewToken";
import { createVideoReview } from "../../utils/videoReviewAdminApi";
import { emptyVideoReviewFormValues, validateVideoReviewForm } from "../../utils/videoReviewForm";
import { useTranslation } from "../../i18n";
import "../delivery/delivery.css";

const { Title, Paragraph } = Typography;

function VideoReviewAdmin() {
    const { t } = useTranslation();
    const [values, setValues] = useState(emptyVideoReviewFormValues);
    const [generatedUrl, setGeneratedUrl] = useState("");
    const [generatedVideoName, setGeneratedVideoName] = useState("");
    const [expiryPreview, setExpiryPreview] = useState("");
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState("");

    const handleField = useCallback((field, value) => {
        setValues((prev) => ({ ...prev, [field]: value }));
    }, []);

    const handleGenerate = useCallback(async () => {
        setError("");
        setGeneratedUrl("");
        setGeneratedVideoName("");
        setExpiryPreview("");

        let normalized;
        try {
            normalized = validateVideoReviewForm(values, t);
        } catch (err) {
            setError(err.message || t("videoReview.admin.generateFailed"));
            return;
        }

        setGenerating(true);
        try {
            const created = await createVideoReview(normalized);
            setGeneratedUrl(buildVideoReviewUrl(created.id));
            setGeneratedVideoName(normalized.videoName);
            setExpiryPreview(values.expiry.format("YYYY-MM-DD HH:mm"));
        } catch (err) {
            setError(err.message || t("videoReview.admin.generateFailed"));
        } finally {
            setGenerating(false);
        }
    }, [values, t]);

    const handleCopy = useCallback(async () => {
        if (!generatedUrl) {
            return;
        }
        try {
            await copyToClipboard(generatedUrl);
            message.success(t("videoReview.admin.linkCopied"));
        } catch {
            message.error(t("videoReview.admin.copyFailed"));
        }
    }, [generatedUrl, t]);

    return (
        <>
            <PageTitle>
                <Navigation />
            </PageTitle>
            <BackHomeIcon to={PANEL_PATH} />

            <div className="delivery-page">
                <div className="custom-h1">
                    <span>{t("videoReview.admin.title")}</span>
                </div>
                <Paragraph style={{ textAlign: "center", color: "var(--color-text-muted)" }}>
                    {t("videoReview.admin.subtitle")}
                </Paragraph>
                <Paragraph
                    style={{
                        textAlign: "center",
                        color: "var(--color-text-subtle)",
                        fontSize: "0.9rem",
                        maxWidth: 560,
                        margin: "0 auto 1.5rem",
                    }}
                >
                    {t("videoReview.admin.description")}
                </Paragraph>

                <div className="delivery-admin-section">
                    <VideoReviewFormFields values={values} onChange={handleField} />

                    {error && (
                        <Paragraph type="danger" style={{ marginBottom: "1rem" }}>
                            {error}
                        </Paragraph>
                    )}

                    <div className="delivery-admin-actions">
                        <Button type="primary" loading={generating} onClick={handleGenerate}>
                            {t("videoReview.admin.generate")}
                        </Button>
                    </div>

                    {generatedUrl && (
                        <div className="delivery-admin-result">
                            <Title level={5}>{t("videoReview.admin.resultTitle")}</Title>
                            {expiryPreview && (
                                <p className="delivery-admin-preview">
                                    {t("videoReview.admin.expiresAt", { time: expiryPreview })}
                                </p>
                            )}
                            {generatedVideoName && (
                                <p className="delivery-admin-preview">{generatedVideoName}</p>
                            )}
                            <div className="delivery-admin-result__url">{generatedUrl}</div>
                            <div className="delivery-admin-actions">
                                <Button type="primary" onClick={handleCopy}>
                                    {t("videoReview.admin.copyLink")}
                                </Button>
                            </div>

                            <div className="delivery-admin-poster">
                                <Title level={5}>{t("videoReview.admin.posterTitle")}</Title>
                                <Paragraph type="secondary" style={{ marginBottom: "1rem" }}>
                                    {t("videoReview.admin.posterHint")}
                                </Paragraph>
                                <VideoReviewSharePanel
                                    url={generatedUrl}
                                    videoName={generatedVideoName}
                                    expiresAtLabel={expiryPreview}
                                    compact
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <Footer />
        </>
    );
}

export default VideoReviewAdmin;
